<template>
  <div v-if="payment || refund || netSummary" class="container">
    <!-- Summary Cards -->
    <div v-if="payment" class="row">
      <div class="col-12">
        <h5 class="text-uppercase">Payment Order Overview</h5>
      </div>
      <!-- Total Orders -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body justify-content-center">
            <ShoppingCartIcon size="32" class="text-primary mb-2" />
            <h1 class="font-weight-bolder mb-0">
              {{ payment.numberOfTotalOrders }}
            </h1>
            <h6 class="text-uppercase text-muted mt-1">Total Orders</h6>
          </div>
        </div>
      </div>

      <!-- Pending Transactions -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body">
            <ClockIcon size="32" class="text-warning mb-2" />
            <h1 class="font-weight-bolder mb-0">
              {{ payment.numberOfPendingTransactions }}
            </h1>
            <h6 class="text-uppercase text-muted mt-1">Pending Transactions</h6>
          </div>
        </div>
      </div>

      <!-- Paid Transactions -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body">
            <DollarSignIcon size="32" class="text-success mb-2" />
            <h1 class="font-weight-bolder mb-0">
              {{ payment.numberOfPaidTransactions }}
            </h1>
            <h6 class="text-uppercase text-muted mt-1">Paid Transactions</h6>
          </div>
        </div>
      </div>

      <!-- Success Rate with ApexChart -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body">
            <!-- <TrendingUpIcon size="32" class="text-info mb-2" /> -->
            <h6 class="text-uppercase text-muted mb-2">Success Rate</h6>
            <vue-apex-charts
              type="radialBar"
              height="100"
              :options="successChartOptions"
              :series="[parseFloat(payment.successRate)]"
            />
            <div class="mt-2 fw-bold">{{ payment.successRate }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- REFUND SECTION -->
    <div v-if="refund" class="row">
      <div class="col-12">
        <h5 class="text-uppercase">Refund Request Overview</h5>
      </div>

      <!-- Total Refund Requests -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body">
            <RefreshCcwIcon size="32" class="text-primary mb-2" />
            <h1 class="font-weight-bolder mb-0">
              {{ refund.numberOfTotalRefundRequests }}
            </h1>
            <h6 class="text-uppercase text-muted mt-1">
              Total Refund Requests
            </h6>
          </div>
        </div>
      </div>

      <!-- Pending Refunds -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body">
            <ClockIcon size="32" class="text-warning mb-2" />
            <h1 class="font-weight-bolder mb-0">
              {{ refund.numberOfPendingRefunds }}
            </h1>
            <h6 class="text-uppercase text-muted mt-1">Pending Refunds</h6>
          </div>
        </div>
      </div>

      <!-- Refunded Transactions -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body">
            <XCircleIcon size="32" class="text-success mb-2" />
            <h1 class="font-weight-bolder mb-0">
              {{ refund.numberOfRefundedTransactions }}
            </h1>
            <h6 class="text-uppercase text-muted mt-1">
              Refunded Transactions
            </h6>
          </div>
        </div>
      </div>

      <!-- Success Rate -->
      <div class="col-md-3 mb-2">
        <div class="card shadow-sm border-0 text-center h-100">
          <div class="card-body">
            <!-- <TrendingDownIcon size="32" class="text-danger mb-2" /> -->
            <h6 class="text-uppercase text-muted mb-2">Success Rate</h6>
            <vue-apex-charts
              type="radialBar"
              height="100"
              :options="refundChartOptions"
              :series="[parseFloat(refund.successRate)]"
            />
            <div class="mt-2 fw-bold">{{ refund.successRate }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Net Summary -->
    <div class="row">
      <div class="col-12">
        <h5 class="text-uppercase">Collection Summary</h5>
      </div>
      <!-- Gross Summary -->
      <div v-if="payment" class="col-md-4 mb-2">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-uppercase text-muted mb-2">Gross Revenue</h6>
            <div class="row d-flex">
              <ul class="list-group list-group-flush col-6">
                <li
                  v-for="(amount, currency) in payment.revenueByCurrency"
                  :key="currency"
                  class="list-group-item d-flex justify-content-between"
                >
                  <h1 class="font-weight-bolder mb-0">
                    {{ amount }}
                  </h1>
                  <span class="align-self-center">({{ currency }})</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Refund Summary -->
      <div
        v-if="Object.keys(refund.refundAmountByCurrency).length !== 0"
        class="col-md-4 mb-2"
      >
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-uppercase text-muted mb-2">Refund Summary</h6>
            <div class="row d-flex">
              <ul class="list-group list-group-flush col-6">
                <li
                  v-for="(amount, currency) in refund.refundAmountByCurrency"
                  :key="currency"
                  class="list-group-item d-flex justify-content-between"
                >
                  <h1 class="font-weight-bolder mb-0">
                    {{ amount }}
                  </h1>
                  <span class="align-self-center">({{ currency }})</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Net Summary -->
      <div v-if="netSummary" class="col-md-4 mb-2">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-uppercase text-muted mb-2">Net Summary</h6>
            <div class="row d-flex">
              <ul class="list-group list-group-flush col-6">
                <li
                  v-for="(amount, currency) in netSummary.netByCurrency"
                  :key="currency"
                  class="list-group-item d-flex justify-content-between"
                >
                  <h1 class="font-weight-bolder mb-0">
                    {{ amount }}
                  </h1>
                  <span class="align-self-center">({{ currency }})</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Net Summary -->
    <!-- <div v-if="netSummary" class="card shadow-sm border-0 mt-3">
      <div class="card-body">
        <h6 class="text-uppercase text-muted mb-3">
          Net Summary (By Currency)
        </h6>
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th>Amount</th>
              <th>Currency</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(amount, currency) in netSummary.netByCurrency"
              :key="currency"
            >
              <td>{{ amount }}</td>
              <td>{{ currency }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div> -->
  </div>
</template>

<script>
import { Bar } from "vue-chartjs";
import VueApexCharts from "vue-apexcharts";
import {
  ShoppingCartIcon,
  ClockIcon,
  DollarSignIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  RefreshCcwIcon,
  XCircleIcon,
  BarChart2Icon,
} from "vue-feather-icons";

import { BDropdown, BDropdownItem, BButton, BFormGroup } from "bootstrap-vue";

export default {
  name: "FinanceDashboard",
  components: {
    barChart: {
      extends: Bar,
      props: ["chartData"],
      mounted() {
        this.renderChart(this.chartData, {
          responsive: true,
          maintainAspectRatio: false,
          scales: { y: { beginAtZero: true } },
        });
      },
    },
    VueApexCharts,
    ShoppingCartIcon,
    ClockIcon,
    DollarSignIcon,
    TrendingUpIcon,
    TrendingDownIcon,
    RefreshCcwIcon,
    XCircleIcon,
    BarChart2Icon,
  },
  props: {
    data: { type: Object, required: true },
  },
  computed: {
    payment() {
      return this.data.payment;
    },
    refund() {
      return this.data.refund;
    },
    netSummary() {
      return this.data.netSummary;
    },
    successChartOptions() {
      return {
        chart: {
          sparkline: { enabled: true },
        },
        plotOptions: {
          radialBar: {
            hollow: { size: "60%" },
            dataLabels: {
              show: false,
            },
          },
        },
        colors: ["#10B981"],
        stroke: { lineCap: "round" },
      };
    },
    refundChartOptions() {
      return {
        chart: { sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            hollow: { size: "60%" },
            dataLabels: { show: false },
          },
        },
        colors: ["#EF4444"], // red tone for refunds
        stroke: { lineCap: "round" },
      };
    },
  },
  watch: {
    data: {
      deep: true,
      immediate: true,
      handler(newData) {
        this.updateRevenueChart(newData.payment?.revenueByCurrency);
      },
    },
  },

  methods: {
    updateRevenueChart(revenueByCurrency) {
      if (!revenueByCurrency) {
        this.revenueChartData = null;
        return;
      }

      const labels = Object.keys(revenueByCurrency);
      const values = Object.values(revenueByCurrency).map((v) => parseFloat(v));

      // Generate dynamic color palette
      const colors = this.generateColors(labels.length);

      this.revenueChartData = {
        labels,
        datasets: [
          {
            label: "Revenue",
            backgroundColor: colors,
            data: values,
          },
        ],
      };
    },
    generateColors(count) {
      const baseColors = [
        "#4caf50",
        "#2196f3",
        "#ff9800",
        "#e91e63",
        "#9c27b0",
        "#00bcd4",
        "#8bc34a",
        "#ffc107",
        "#795548",
        "#607d8b",
      ];

      // If count <= baseColors.length, just slice
      if (count <= baseColors.length) {
        return baseColors.slice(0, count);
      }

      // Otherwise, generate random colors for extras
      const colors = [...baseColors];
      while (colors.length < count) {
        const randomColor = `hsl(${Math.floor(Math.random() * 360)}, 70%, 50%)`;
        colors.push(randomColor);
      }
      return colors;
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 1rem;
}
.card-body {
  padding: 1.5rem;
}
.card {
  border-radius: 1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}
@media (max-width: 1080px) {
  h1 {
    font-size: 1.5rem;
  }
}

@media (max-width: 576px) {
  h1 {
    font-size: 1.2rem;
  }
}
</style>
